<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SEO from '@/components/SEO.vue';
import SignInCard from '@/components/SignInCard.vue';
import { getUser } from '@/lib/user';

const route = useRoute();
const router = useRouter();

const loginType = ref(route.path === '/sign-in' ? 'sign-in' : 'sign-up'); // Set based on current route path
const showEmail = ref(false);
const showPhone = ref(false);
const isLogin = computed(() => loginType.value === 'sign-in');
const animationReady = ref(false);

function afterAuth(token) {
  if (route.query.target === 'client') {
    window.location.href = `seede://auth?token=${token}`;
  } else if (route.query.back) {
    router.push(route.query.back);
  } else {
    router.push('/');
  }
  getUser();
}

onMounted(() => {
  setTimeout(() => {
    animationReady.value = true;
  }, 100);
});
</script>

<template>
  <div class="min-h-screen relative overflow-hidden" style="background: #f6fcff">
    <!-- SEO -->
    <SEO
      :title="`${isLogin ? 'Sign In' : 'Sign Up'} - Seede AI`"
      :description="`${
        isLogin ? 'Sign in to your Seede.ai account' : 'Create a new Seede.ai account'
      } to access powerful AI tools and templates.`"
      :keywords="`sign in,sign up,account,login,register,seede ai`"
    />

    <!-- Main Content - Split Layout -->
    <div class="flex flex-col md:flex-row min-h-screen">
      <!-- Left Side - Login Form -->
      <div
        class="w-full md:w-1/2 flex flex-col justify-center items-center px-6 py-12 md:py-0 relative z-10"
      >
        <div class="w-full max-w-md">
          <!-- Brand Logo for Mobile -->
          <div class="md:hidden mb-8 text-center">
            <router-link to="/">
              <img src="/images/Seede_logotype.svg" alt="Seede.ai Logo" class="h-8 mx-auto" />
            </router-link>
          </div>

          <SignInCard
            v-model:loginType="loginType"
            v-model:show-email="showEmail"
            v-model:show-phone="showPhone"
            :after-auth="afterAuth"
            :show-case="false"
          />

          <!-- Footer Links for Mobile -->
          <div class="mt-8 md:hidden">
            <div class="flex justify-center space-x-6 text-sm">
              <a
                href="/privacy"
                class="text-black/60 hover:text-black transition-colors duration-300"
                >{{ $t('app.privacy') }}</a
              >
              <a
                href="/terms"
                class="text-black/60 hover:text-black transition-colors duration-300"
                >{{ $t('app.terms') }}</a
              >
              <a
                href="/contact"
                class="text-black/60 hover:text-black transition-colors duration-300"
                >{{ $t('landing.contactUs') }}</a
              >
            </div>
            <p class="text-black/60 text-sm text-center mt-4">
              &copy; {{ new Date().getFullYear() }} Seede.ai
            </p>
          </div>
        </div>
      </div>

      <!-- Right Side - Elegant Background -->
      <div
        class="hidden md:block md:w-1/2 relative"
        style="
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.2) 100%
          );
        "
      >
        <!-- Brand Logo -->
        <div class="absolute top-8 left-8 z-10">
          <router-link to="/">
            <img
              src="/images/seede-black.svg"
              alt="Seede.ai Logo"
              class="h-8 opacity-80 hover:opacity-100 transition-opacity duration-300"
            />
          </router-link>
        </div>

        <!-- Subtle Geometric Background -->
        <div class="absolute inset-0 overflow-hidden">
          <!-- Minimalist Circles -->
          <div
            class="absolute top-1/4 right-1/3 w-96 h-96 rounded-full blur-3xl animate-pulse"
            style="background: rgba(0, 0, 0, 0.02); animation-duration: 4s"
          ></div>
          <div
            class="absolute bottom-1/3 left-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse"
            style="background: rgba(0, 0, 0, 0.015); animation-duration: 6s; animation-delay: 1s"
          ></div>

          <!-- Subtle Grid Pattern -->
          <div class="absolute inset-0 opacity-[0.02]">
            <div
              class="w-full h-full"
              style="
                background-image: radial-gradient(circle, #000000 1px, transparent 1px);
                background-size: 60px 60px;
              "
            ></div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="absolute inset-0 flex items-center justify-center p-16">
          <div class="text-center max-w-lg">
            <!-- Case Image with Enhanced Styling -->
            <div class="mb-12 relative">
              <div
                class="absolute -inset-4 rounded-3xl blur-xl"
                style="background: rgba(0, 0, 0, 0.03)"
              ></div>
              <img
                src="/images/case.webp"
                alt="Authentication Illustration"
                class="relative max-w-full max-h-80 object-contain mx-auto"
                style="filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1))"
              />
            </div>

            <!-- Elegant Text Content -->
            <div class="space-y-4">
              <h2 class="text-[2rem] font-light text-black tracking-tight leading-tight">
                {{ isLogin ? $t('signIn.welcomeBack') : $t('signIn.joinToday') }}
              </h2>
              <p
                class="text-black/60 text-lg leading-relaxed font-light max-w-md mx-auto"
                v-html="$t('signIn.accessTools')"
              ></p>
            </div>
          </div>
        </div>

        <!-- Minimalist Footer -->
        <div class="absolute bottom-8 left-8 right-8">
          <div class="flex justify-between items-center text-black/50 text-sm">
            <span class="font-light">&copy; {{ new Date().getFullYear() }} Seede.ai</span>
            <div class="flex space-x-2"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
